import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "../lib/api";
import { Footer } from "../components/Footer";
import { Reveal, Label } from "../components/anim";
import { SubscribeBar } from "../components/SubscribeBar";
import { CategoryChips, PostCard } from "../components/blog";
import { categoryFromSlug } from "../lib/blogCategories";
import useSeo from "../hooks/useSeo";
import { ArrowLeft, Loader2, PenLine } from "lucide-react";

export default function BlogCategory() {
  const { slug } = useParams();
  const category = categoryFromSlug(slug);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(Boolean(category));

  useSeo({
    title: category ? `${category} — Field Notes | Andres Campos` : "Topic Not Found | Andres Campos",
    description: category
      ? `Articles on ${category} — pre-development and permitting insight from Andres Campos.`
      : "This blog topic does not exist.",
    type: "website",
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!category) return;
    setLoading(true);
    (async () => {
      try {
        const res = await http.get("/posts", { params: { category } });
        setPosts(res.data);
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#EFEBDE] px-6 text-center">
        <h1 className="font-display font-semibold text-4xl tracking-tight text-[#16281C]">Topic not found</h1>
        <Link to="/blog" className="mt-8 inline-flex items-center gap-2 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-[#367A49] transition-colors">
          <ArrowLeft size={15} /> Back to blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <main className="min-h-screen bg-[#EFEBDE] pt-32 pb-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Reveal>
            <Label>Topic</Label>
            <h1 data-testid="category-title" className="mt-6 font-display font-semibold uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9] text-[#16281C]">
              {category}
            </h1>
            <p className="mt-6 font-body text-base md:text-lg text-[#4C5C50] max-w-xl">
              Field notes filed under {category}.
            </p>
          </Reveal>

          <CategoryChips active={category} />

          <div className="mt-12">
            {loading ? (
              <div className="flex justify-center py-24"><Loader2 size={30} className="animate-spin text-[#2C5F3A]" /></div>
            ) : posts.length === 0 ? (
              <div className="text-center py-24">
                <PenLine size={40} className="text-[#8B978C] mx-auto" strokeWidth={1.25} />
                <p className="mt-5 font-body text-[#4C5C50]">No published posts in this topic yet. Check back soon.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((p, i) => <PostCard key={p.id} p={p} i={i} />)}
              </div>
            )}
          </div>
          <div className="mt-16"><SubscribeBar /></div>
        </div>
      </main>
      <Footer />
    </>
  );
}
